import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchGuests,
  deleteGuest,
  getGuestById,
  clearSelectedGuest,
} from "@/store/altingross/guests";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Loader2, User, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import GuestDetailModal from "@/components/shared/GuestDetailModal";

const PAGE_SIZE = 20;

export default function Guests() {
  const dispatch = useDispatch();
  const { list, loading, pagination, deletingId, selectedGuest, detailLoading } = useSelector(
    (s) => s.guests
  );

  const [page, setPage] = useState(1);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    (p = page) => dispatch(fetchGuests({ page: p, size: PAGE_SIZE })),
    [dispatch, page]
  );

  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleViewDetail = async (id) => {
    setDetailModalOpen(true);
    await dispatch(getGuestById(id));
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    dispatch(clearSelectedGuest());
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await dispatch(deleteGuest(deleteTarget.id));
    if (!res.error) {
      toast.success("Misafir kaydı silindi.");
      load(page);
    } else {
      toast.error("Silme başarısız.");
    }
    setDeleteTarget(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        testId="guests-header"
        title="Misafir Ziyaretçiler"
        description="Siteyi ziyaret eden misafirlerin (guest) kayıtlarını görüntüleyin."
      />

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Table header */}
        <div className="grid grid-cols-[auto_1fr_1fr_1fr_auto_auto] items-center border-b border-gray-100 px-4 py-2.5 gap-4">
          <span className="text-xs font-semibold text-black uppercase tracking-wide">IP</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block">Cihaz / OS</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden md:block">Tarayıcı</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden lg:block">Ziyaret Tarihi</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide">Durum</span>
          <span></span>
        </div>

        {loading && list.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Yükleniyor…
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            Henüz misafir kaydı bulunmuyor.
          </div>
        ) : (
          <ul>
            {list.map((guest, idx) => (
              <li
                key={guest.id}
                className={`grid grid-cols-[auto_1fr_1fr_1fr_auto_auto] items-center px-4 py-2.5 gap-4 ${
                  idx !== list.length - 1 ? "border-b border-gray-100" : ""
                } hover:bg-gray-50 transition-colors`}
              >
                <span className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <User className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                  {guest.ipAddress || "Bilinmiyor"}
                </span>
                <span className="text-sm text-gray-600 hidden sm:block">
                  {guest.deviceType} / {guest.os}
                </span>
                <span className="text-sm text-gray-600 hidden md:block truncate" title={guest.browser}>
                  {guest.browser}
                </span>
                <span className="text-xs text-gray-400 hidden lg:block whitespace-nowrap">
                  {guest.visitedAt
                    ? new Date(guest.visitedAt).toLocaleString("tr-TR")
                    : "—"}
                </span>
                <span className="text-xs">
                  {guest.isBot ? (
                    <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Bot</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-600 font-medium">Kullanıcı</span>
                  )}
                </span>
                <span className="flex items-center gap-1 justify-end">
                  {deletingId === guest.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-blue-500"
                        onClick={() => handleViewDetail(guest.id)}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-red-500"
                        onClick={() => setDeleteTarget(guest)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {list.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
            <span>
              Toplam {pagination.count} kayıt — Sayfa {pagination.page}/{pagination.totalPages || 1}
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Önceki
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <GuestDetailModal 
        open={detailModalOpen} 
        onOpenChange={setDetailModalOpen} 
      />

      {/* Delete Alert */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaydı sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.ipAddress}</strong> IP adresine sahip ziyaretçi kaydı kalıcı olarak silinecek. Emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
